/**
 * Provider return-URL adapters.
 *
 * Wompi appends `idTransaccion`. PayPal appends `token`. A future Stripe
 * return would append `payment_intent`. Screens only ever see
 * `transactionId` + `provider` + `rentalId` / `bookingId`.
 */
export type PaymentReturnParams = {
  transactionId: string | null
  rentalId: string | null
  bookingId: string | null
  provider: string | null
  cancelled: boolean
}

export type PaymentReturnAdapter = {
  id: string
  transactionIdKeys: readonly string[]
}

export const PAYMENT_RETURN_ADAPTERS: readonly PaymentReturnAdapter[] = [
  { id: 'paypal', transactionIdKeys: ['token', 'transactionId'] },
  { id: 'wompi', transactionIdKeys: ['idTransaccion', 'id_transaccion', 'transactionId'] },
  { id: 'generic', transactionIdKeys: ['transactionId'] },
]

function firstParam(source: Record<string, unknown>, keys: readonly string[]): string | null {
  for (const key of keys) {
    const value = source[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
    if (Array.isArray(value) && typeof value[0] === 'string' && value[0].trim()) {
      return value[0].trim()
    }
  }
  return null
}

export function parsePaymentReturnParams(
  raw: Record<string, unknown> | URLSearchParams,
): PaymentReturnParams {
  const source: Record<string, unknown> =
    raw instanceof URLSearchParams ? Object.fromEntries(raw.entries()) : raw

  const hinted =
    typeof source.provider === 'string' && source.provider.trim()
      ? source.provider.trim().toLowerCase()
      : null

  const adapters = hinted
    ? [
        ...PAYMENT_RETURN_ADAPTERS.filter((adapter) => adapter.id === hinted),
        ...PAYMENT_RETURN_ADAPTERS.filter((adapter) => adapter.id !== hinted),
      ]
    : PAYMENT_RETURN_ADAPTERS

  let transactionId: string | null = null
  let provider = hinted
  for (const adapter of adapters) {
    const found = firstParam(source, adapter.transactionIdKeys)
    if (found) {
      transactionId = found
      if (!provider && adapter.id !== 'generic') provider = adapter.id
      break
    }
  }

  const cancelledRaw = firstParam(source, ['cancelled', 'cancel'])
  return {
    transactionId,
    rentalId: firstParam(source, ['rentalId', 'rental_id']),
    bookingId: firstParam(source, ['bookingId', 'booking_id']),
    provider,
    cancelled: cancelledRaw === '1' || cancelledRaw === 'true',
  }
}

/** Query keys the HTTPS hop writes so the app can stay provider-agnostic. */
export function paymentReturnQuery(params: {
  transactionId?: string
  rentalId?: string
  bookingId?: string
  provider?: string
}): Record<string, string> {
  const query: Record<string, string> = {}
  if (params.transactionId) query.transactionId = params.transactionId
  if (params.rentalId) query.rentalId = params.rentalId
  if (params.bookingId) query.bookingId = params.bookingId
  if (params.provider) query.provider = params.provider
  return query
}
